/**
 * ImpersonationBanner
 *
 * Renders a sticky banner at the top of the viewport when an admin is
 * impersonating another user. The banner is mounted in __root.tsx so it
 * persists across ALL route navigations.
 *
 * No countdown by design — matches the industry pattern (GitHub, Vercel,
 * Stripe). When the session expires, the next API call surfaces it.
 */

import { AlertTriangle } from 'lucide-react'
import { Button } from '~/client/components/ui/button'
import { useAuthStore } from '~/client/common/stores/auth'

export default function ImpersonationBanner() {
  const viewAsUser = useAuthStore((s) => s.viewAsUser)
  const stopImpersonation = useAuthStore((s) => s.stopImpersonation)

  if (!viewAsUser) return null

  const displayName = [viewAsUser.first_name, viewAsUser.last_name].filter(Boolean).join(' ')

  const handleStop = () => {
    void stopImpersonation().then(() => {
      window.location.href = '/'
    })
  }

  return (
    <div
      className="sticky top-0 z-[9999] flex items-center justify-between gap-3 border-b border-amber-300 bg-amber-100 px-4 py-2 text-amber-900"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
        <span>
          Viewing as <strong>{displayName}</strong>
        </span>
      </div>

      <Button
        variant="outline"
        size="sm"
        className="h-7 shrink-0 border-amber-600 bg-white text-amber-900 hover:bg-amber-50"
        onClick={handleStop}
      >
        Stop
      </Button>
    </div>
  )
}
